import { DSOMetrics } from '@dill/dill-shared';

interface DSOMetricsCardProps {
  dsoMetrics?: DSOMetrics | null;
}

function DSOMetricsCard({ dsoMetrics }: DSOMetricsCardProps) {
  const currentDSO = dsoMetrics?.currentDSO ?? 0;
  const previousDSO = dsoMetrics?.previousPeriodDSO;
  const dsoChange = previousDSO !== undefined ? currentDSO - previousDSO : null;
  const isImproving = dsoChange !== null && dsoChange < 0;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Days Sales Outstanding (DSO)</h3>
        <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
          <svg
            className="w-5 h-5 text-blue-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
      </div>

      <div className="flex items-end gap-4">
        <div>
          <p className="text-sm text-gray-500">Current DSO</p>
          <p className="text-4xl font-bold text-gray-900">{currentDSO.toFixed(1)}</p>
          <p className="text-sm text-gray-500">days</p>
        </div>

        {dsoChange !== null && (
          <div className="flex items-center gap-1 mb-2">
            <span
              className={`text-lg font-medium ${isImproving ? 'text-green-600' : 'text-red-600'}`}>
              {isImproving ? '↓' : '↑'}
            </span>
            <span
              className={`text-sm font-medium ${isImproving ? 'text-green-600' : 'text-red-600'}`}>
              {Math.abs(dsoChange).toFixed(1)} days
            </span>
            <span className="text-xs text-gray-500 ml-1">vs prev period</span>
          </div>
        )}
      </div>

      {dsoMetrics?.dsoTrend && dsoMetrics.dsoTrend.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <p className="text-xs text-gray-500 mb-2">Monthly Trend</p>
          <div className="flex items-end gap-1 h-12">
            {dsoMetrics.dsoTrend.map((point, index) => {
              const maxDSO = Math.max(...dsoMetrics.dsoTrend.map((p) => p.dso));
              const height = maxDSO > 0 ? (point.dso / maxDSO) * 100 : 0;
              return (
                <div
                  key={index}
                  className="flex-1 bg-blue-200 rounded-t hover:bg-blue-300 transition-colors cursor-pointer"
                  style={{ height: `${Math.max(height, 5)}%` }}
                  title={`${point.month}: ${point.dso.toFixed(1)} days`}
                />
              );
            })}
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-xs text-gray-400">
              {dsoMetrics.dsoTrend[0]?.month ? dsoMetrics.dsoTrend[0]?.month.split('-')[1] : ''}
            </span>
            <span className="text-xs text-gray-400">
              {dsoMetrics.dsoTrend[dsoMetrics.dsoTrend.length - 1]?.month
                ? dsoMetrics.dsoTrend[dsoMetrics.dsoTrend.length - 1]?.month?.split('-')[1]
                : ''}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default DSOMetricsCard;
